/* eslint-disable indent */
/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable no-nested-ternary */

import Task from '@mui/icons-material/Task';
import Today from '@mui/icons-material/Today';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';

import { alpha } from '@mui/material/styles';
// components
import {
  AccessTime,
  AttachFile,
  Close,
  LocationOn,
} from '@mui/icons-material';

import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { patchEvent } from '@pingum/app-state-management/event/actions';
import { editEventStatus } from '@pingum/app-state-management/eventStatus/actions';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import AttachFileModal from '../../AttachFileModal';
import EventUpload from '../../EventUpload';
import Property from '../../Property';

// utils
import {
  formatDateString,
  fromPickerValue,
  toMomentValue,
} from '../../../utils/dateHelper';

// local event page helper components
import EventActionsMenu from '../EventActionsMenu';

import { TASK_MODAL_TYPE } from '../../../redux/app/types';
import { changeEventStatus } from '../../../redux/event/actions';
import { getErrorMessage } from '../../../redux/networkDispatcher';
import { primary } from '../../../utils/brandColors';
import { getBillablePriceError } from '../../../utils/billablePrice';
import { getContrast } from '../../../utils/scheduleHelper';
import { getLocationForTags } from '../../../utils/tagsHelper';
import BarnHorsesAutocomplete from '../../Autocompletes/BarnHorses';
import LocationAutocomplete from '../../Autocompletes/Locations';
import HorseIcon from '../../CustomIcons/Horse';
import HorseTaskReminders from '../../HorseTaskReminders';
import HorseTaskReminderIndicator from '../../HorseTaskReminders/Indicator';
import HeaderSection from '../../Schedule/AddEditScheduleModal/components/HeaderSection';
import EditTaskFields from '../EditTaskFields';
import TaskHighlightComponent from '../TaskHighlight';

const startingValidation = {
  name: true,
  completeDate: true,
  hasChanged: false,
};

const EventDataCardComponent = ({
  selectedEvent,
  schedule,
  isCalendarEvent,
  classes,
  isRestricted,
  canAttachFiles,
  showLoadingIndicator,
  isUpdatingEvent,
  selectedBarn,
  getEvent,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const isChangingStatus = useSelector((state) => !!state.event?.isChangingStatus);
  const mutationPending = React.useRef(false);
  const currentContext = `${selectedBarn?.tenant}:${selectedBarn?.id}:${selectedEvent?.id}`;
  const liveContext = React.useRef(currentContext);
  const activeMutation = React.useRef(null);
  if (liveContext.current !== currentContext && activeMutation.current?.targetContext !== currentContext) {
    activeMutation.current = null;
  }
  liveContext.current = currentContext;
  const mounted = React.useRef(false);
  const [savingTask, setSavingTask] = React.useState(false);
  const [mutationError, setMutationError] = React.useState(null);
  const taskBusy = isUpdatingEvent || isChangingStatus || savingTask;

  React.useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  React.useEffect(() => {
    if (!activeMutation.current) {
      mutationPending.current = false;
      setSavingTask(false);
      setMutationError(null);
    }
  }, [currentContext]);

  const handleSafeClose = () => {
    if (!mutationPending.current && !isUpdatingEvent && !isChangingStatus && handleClose) handleClose();
  };

  const handleMutationChange = (pending) => {
    mutationPending.current = pending;
    setSavingTask(pending);
  };

  // React state
  const [editMode, setEditMode] = React.useState(false)
  const [name, setName] = React.useState('');
  const [selectedEventBarnHorse, setSelectedEventBarnHorse] = React.useState(null);
  const [selectedEventLocation, setSelectedEventLocation] = React.useState(null);
  const [startDate, setStartDate] = React.useState(new Date());
  const [description, setDescription] = React.useState('')
  const [eventData, setEventData] = React.useState({})
  const [completeDate, setCompleteDate] = React.useState(null);
  const [currentStatusAssignment, setCurrentStatusAssignment] = React.useState(null);
  const [dueDate, setDueDate] = React.useState(null);
  const [tags, setTags] = React.useState([]);
  const [scheduleAssignment, setScheduleAssignment] = React.useState(null);
  const [validation, setValidation] = React.useState(startingValidation);

  const [openAttachFileModal, setOpenAttachFileModal] = React.useState(false);

  const openModalType = useSelector((state) => state.app.openModalType);
  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);
  const event = useSelector((state) => state.schedule.event);
  const eventTasks = useSelector((state) => state.schedule.eventTasks)
  const locationsMap = useSelector((state) => state.location.locationsMap)

  const resetData = () => {
    setEditMode(false);
    setName(selectedEvent.name || '')
    setDescription(selectedEvent.description || '')
    setCompleteDate(selectedEvent.completeDate || null)
    setDueDate(selectedEvent.dueDate || null)
    setTags(selectedEvent.tags || [])
    setEventData(selectedEvent.eventData || '')
    setScheduleAssignment(selectedEvent.scheduleAssignment || '');
    setCurrentStatusAssignment((selectedEvent && selectedEvent.currentEventStatus && selectedEvent.currentEventStatus.assignment) || null)
    setValidation(startingValidation);

    const horseTag = (selectedEvent.tags || []).find((tag) => tag.tagType?.id === selectedBarn.barnMetadata.horseTagTypeId);
    const eventHorse = horseTag && (barnHorsesMap[horseTag.id] || [])
      .find((horse) => Number(horse.pingumTagId) === Number(horseTag.id));
    setSelectedEventBarnHorse(eventHorse || null);
    setSelectedEventLocation((selectedEvent && selectedEvent.id
      && !eventHorse && getLocationForTags(
        selectedEvent.tags,
        selectedBarn.barnMetadata.locationTagTypeId,
        locationsMap,
      )) || null);
    setStartDate((selectedEvent && selectedEvent.startDate) || new Date())
  }

  React.useEffect(() => {
    resetData();
  }, [selectedEvent]);

  React.useEffect(() => {
    // Capture when assignment changes so we can show save button
    if (currentStatusAssignment) {
      setValidation({
        ...validation,
        hasChanged: true,
      })
    }
  }, [currentStatusAssignment]);

  const handleSaveEdit = async () => {
    if (mutationPending.current || isUpdatingEvent || isChangingStatus) return;
    const priceError = getBillablePriceError(eventData);
    if (priceError) {
      setMutationError(priceError);
      return;
    }
    const task = selectedEvent;
    const mutation = { targetContext: currentContext };
    activeMutation.current = mutation;
    const isCurrent = () => mounted.current && activeMutation.current === mutation;
    handleMutationChange(true);
    setMutationError(null);
    const request = {
      name,
      description,
      completeDate,
      tags: tags.map((t) => t.id),
      eventData,
      dueDate,
      scheduleAssignment,
      startDate,
    };

    try {
      await dispatch(patchEvent(task.id, request));
      if (!isCurrent()) return;
      if (currentStatusAssignment) {
        await dispatch(editEventStatus(task.currentEventStatus.id, { assignment: currentStatusAssignment }));
        if (!isCurrent()) return;
        getEvent(task.id);
      }
      if (handleClose) handleClose();
    } catch (error) {
      if (isCurrent()) {
        setMutationError(getErrorMessage({
          error,
          defaultErrorMessage: 'Unable to save the task. Please try again.',
        }));
      }
    } finally {
      if (isCurrent()) {
        activeMutation.current = null;
        handleMutationChange(false);
      }
    }
  };

  const handleEventDataChange = (updatedEventData, autoSave = false) => {
    setEventData(updatedEventData);
    setValidation({
      ...validation,
      hasChanged: true,
    })
    if (autoSave) {
      const priceError = getBillablePriceError(updatedEventData);
      if (priceError) {
        setMutationError(priceError);
        return;
      }
      dispatch(patchEvent(selectedEvent.id, { eventData: updatedEventData }));
    }
  }

  const handleAttachFileClick = () => {
    setOpenAttachFileModal(true);
  }

  const handleCloseAttachFileModal = () => {
    setOpenAttachFileModal(false);
  }

  const handleNameUpdate = (updatedName) => {
    setName(updatedName);
    setValidation({
      ...validation,
      name: updatedName && updatedName.length >= 2 && updatedName.length < 100,
      hasChanged: true,
    })
  }

  const handleScheduleAssignmentChange = (updatedScheduleAssignment) => {
    setScheduleAssignment(updatedScheduleAssignment);
    setValidation({
      ...validation,
      hasChanged: true,
    })
  }

  const handleCompleteDateChange = (updatedCompleteDate) => {
    setCompleteDate(updatedCompleteDate);
    setValidation({
      ...validation,
      completeDate: updatedCompleteDate && updatedCompleteDate.getTime() > new Date(selectedEvent.startDate).getTime(),
      hasChanged: true,
    })
  }

  const handleEventStatus = async (request, replacementTask, keepOpen = false) => {
    if (mutationPending.current || isUpdatingEvent || isChangingStatus) return;
    const task = replacementTask || selectedEvent;
    const priceError = request.completeEvent && getBillablePriceError(task.eventData);
    if (priceError) {
      setMutationError(priceError);
      return;
    }
    const mutation = { targetContext: `${selectedBarn?.tenant}:${selectedBarn?.id}:${task.id}` };
    activeMutation.current = mutation;
    const isCurrent = () => mounted.current && activeMutation.current === mutation;
    const statusId = request.completeEvent
      ? selectedBarn.barnMetadata.statusEndId
      : selectedBarn.barnMetadata.statusPendingId;
    handleMutationChange(true);
    setMutationError(null);
    try {
      await dispatch(patchEvent(task.id, request));
      if (!isCurrent()) return;
      await dispatch(changeEventStatus(task.id, {
        statusId: parseInt(statusId, 10),
        statusComment: null,
        completeEvent: request.completeEvent,
        assignment: (task.currentEventStatus && task.currentEventStatus.assignment) || '',
      }));
      if (isCurrent() && !keepOpen && handleClose) handleClose();
    } catch (error) {
      if (isCurrent()) {
        setMutationError(getErrorMessage({
          error,
          defaultErrorMessage: 'Unable to update the task. Please try again.',
        }));
      }
    } finally {
      if (isCurrent()) {
        activeMutation.current = null;
        handleMutationChange(false);
      }
    }
  };

  const headerBackgroundColor = (schedule && schedule.metadata && schedule.metadata.calendarDisplayColor)
    || (schedule && schedule.eventTemplate && schedule.eventTemplate.eventDataDefaultValues
      && schedule.eventTemplate.eventDataDefaultValues.calendarDisplayColor)
    || (selectedEvent && selectedEvent.eventData && selectedEvent.eventData.Color && selectedEvent.eventData.Color.value)
    || primary.main;
  const headerTextColor = getContrast(headerBackgroundColor);
  const associatedTasks = (eventTasks) || []
  const isAllDayScheduledTask = !!(
    (event && event.allDay)
    || (schedule && schedule.duration === 0)
  );
  const taskDateLabel = isAllDayScheduledTask
    ? 'Task Date'
    : 'Task Date & Time';
  const listItemSx = {
    px: 1,
    py: 0.75,
  };
  const listItemIconSx = {
    minWidth: 32,
    mr: 0.75,
    '& .MuiSvgIcon-root': {
      height: '1.25rem',
      width: '1.25rem',
    },
  };

  const formattedStartDate = selectedEvent && selectedEvent.startDate
    ? formatDateString(selectedEvent.startDate)
    : 'No start date';

  const reminderPairs = tags
    .filter((tag) => tag.tagType?.id === selectedBarn.barnMetadata.horseTagTypeId)
    .map((tag) => ({
      tagId: tag.id,
      eventTemplateId: selectedEvent.eventTemplateId,
    }));

  const taskContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: headerBackgroundColor,
          color: `${headerTextColor} !important`,
          m: 1,
          borderRadius: 4,
          backgroundImage: `linear-gradient(130deg, ${headerBackgroundColor}, ${alpha(headerBackgroundColor, 0.85)})`,
          overflow: 'hidden',
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={2}
            alignItems={{
              xs: 'flex-start',
              sm: 'center',
            }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  backgroundColor: `${headerTextColor}1A`,
                  color: headerTextColor,
                  border: `1px solid ${alpha(headerTextColor, 0.4)}`,
                }}
              >
                {isCalendarEvent
                  ? <Today />
                  : <Task />}
              </Avatar>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Typography
                  component="h3"
                  sx={{
                    margin: 0,
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    lineHeight: '1.9rem',
                    width: '100%',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {selectedEvent.name}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography
                    variant="body2"
                    sx={{ color: alpha(headerTextColor, 0.9) }}
                  >
                    {formattedStartDate}
                  </Typography>
                  {selectedEvent && selectedEvent.id && (
                    <Chip
                      size="small"
                      label={`Task #${selectedEvent.id}`}
                      sx={{
                        backgroundColor: alpha('#000', 0.12),
                        '& .MuiChip-label': { color: `${alpha(headerTextColor, 0.9)} !important` },
                      }}
                    />
                  )}
                </Stack>
              </Box>
            </Stack>
            {
              !isRestricted && (
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                  <EventActionsMenu
                    attachFileClick={handleAttachFileClick}
                    onEdit={() => setEditMode(true)}
                    handleClose={handleSafeClose}
                    editMode={editMode}
                    onMutationChange={handleMutationChange}
                    mutationDisabled={taskBusy}
                    sx={{ color: headerTextColor }}
                  />
                  {
                    handleClose && (
                      <Tooltip title="Close Task">
                        <span>
                          <IconButton onClick={handleSafeClose} disabled={taskBusy} aria-label="Close Task" role="button">
                            <Close sx={{ color: headerTextColor }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )
                  }
                </Stack>
              )
            }
          </Stack>
        </Stack>
      </DialogTitle>
      {mutationError && <Alert severity="error" sx={{ mx: 2 }}>{mutationError}</Alert>}
      <DialogContent
        sx={{
          pb: 0,
          px: 1,
          flex: 1,
        }}
      >
        {
          showLoadingIndicator && (
            <Box
              sx={{
                width: '100%',
                mb: 2,
              }}
            >
              <LinearProgress />
            </Box>
          )
        }

        {
          editMode
            ? (
              <Stack spacing={2}>
                <Paper
                  sx={{ borderRadius: 3 }}
                >
                  <List disablePadding>
                    <ListItem dense sx={listItemSx}>
                      <ListItemIcon sx={listItemIconSx}>
                        {selectedEventBarnHorse
                          ? <HorseIcon />
                          : <LocationOn fontSize="1.25rem" />}
                      </ListItemIcon>
                      <ListItemText>
                        <Stack spacing={1}>
                          {selectedEventBarnHorse && (
                            <BarnHorsesAutocomplete
                              multiple={false}
                              label="Horse"
                              value={selectedEventBarnHorse}
                              blurOnSelect
                              onSelect={(e, newHorse) => {
                                if (newHorse) {
                                  setTags([...tags.filter((t) => t.id !== parseInt(selectedEventBarnHorse.pingumTagId, 10)),
                                  {
                                    id: parseInt(newHorse.pingumTagId, 10),
                                    name: newHorse.horse.name,
                                    tagType: { id: selectedBarn.barnMetadata.horseTagTypeId },
                                  }])
                                  setSelectedEventBarnHorse(newHorse);
                                  setValidation({
                                    ...validation,
                                    hasChanged: true,
                                  });
                                }
                              }}
                              limitTags={1}
                            />
                          )}
                          {selectedEventBarnHorse && (
                            <HorseTaskReminderIndicator
                              tagId={selectedEventBarnHorse.pingumTagId}
                              eventTemplateId={selectedEvent.eventTemplateId}
                            />
                          )}
                          {selectedEventLocation && (
                            <LocationAutocomplete
                              multiple={false}
                              label="Location"
                              value={selectedEventLocation}
                              onSelect={(e, newLocations) => {
                                if (newLocations) {
                                  setTags([...tags.filter((t) => t.id !== parseInt(selectedEventLocation.pingumTagId, 10)),
                                  {
                                    id: parseInt(newLocations.pingumTagId, 10),
                                    name: newLocations.name,
                                    tagType: { id: selectedBarn.barnMetadata.locationTagTypeId },
                                  }])
                                  setSelectedEventLocation(newLocations)
                                  setValidation({
                                    ...validation,
                                    hasChanged: true,
                                  });
                                }
                              }}
                              size="small"
                              limitTags={1}
                            />
                          )}
                        </Stack>
                      </ListItemText>
                    </ListItem>
                    <ListItem dense sx={listItemSx}>
                      <ListItemIcon sx={listItemIconSx}><AccessTime /></ListItemIcon>
                      <ListItemText>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          {isAllDayScheduledTask
                            ? (
                              <DatePicker
                                label={taskDateLabel}
                                slotProps={{ textField: { size: 'small' } }}
                                value={toMomentValue(startDate)}
                                onChange={(newValue) => {
                                  const updatedDate = fromPickerValue(newValue);
                                  if (updatedDate) {
                                    updatedDate.setHours(0, 0, 0, 0);
                                    setStartDate(updatedDate)
                                    setValidation({
                                      ...validation,
                                      hasChanged: true,
                                    });
                                  }
                                }}
                              />
                            )
                            : (
                              <DateTimePicker
                                label={taskDateLabel}
                                slotProps={{ textField: { size: 'small' } }}
                                value={toMomentValue(startDate)}
                                onChange={(newValue) => {
                                  const updatedDate = fromPickerValue(newValue);
                                  if (updatedDate) {
                                    setStartDate(updatedDate)
                                    setValidation({
                                      ...validation,
                                      hasChanged: true,
                                    });
                                  }
                                }}
                              />
                            )}
                        </LocalizationProvider>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Paper>
                <Paper
                  sx={{
                    borderRadius: 3,
                    p: 1,
                  }}
                >
                  <Box
                    sx={{
                      '& .MuiListItem-root': listItemSx,
                      '& .MuiListItemIcon-root': listItemIconSx,
                    }}
                  >
                    <HeaderSection
                      validation={validation}
                      schedule={schedule}
                      event={event}
                      name={name}
                      setName={handleNameUpdate}
                      description={description}
                      setDescription={(newDescription) => {
                        setValidation({
                          ...validation,
                          hasChanged: true,
                        });
                        setDescription(newDescription);
                      }}
                      duration={(event && event.resource && event.resource.duration) || 0}
                      workflows={(event && event.resource && event.resource.workflows) || []}
                      additionalTags={tags}
                      setAdditionalTags={(newTags) => {
                        setValidation({
                          ...validation,
                          hasChanged: true,
                        });
                        setTags(newTags);
                      }}
                      detailedLocation={(event && event.resource && event.resource.location) || ''}
                      assignment={(event && event.resource && event.resource.assignment) || 'Default'}
                      type={(event && event.resource && event.resource.type) || ''}
                      disabledForBeingInPast
                    />
                  </Box>
                </Paper>
                <Paper
                  sx={{
                    borderRadius: 3,
                    p: 1,
                  }}
                >
                  <Box
                    sx={{
                      '& .MuiListItem-root': listItemSx,
                      '& .MuiListItemIcon-root': listItemIconSx,
                    }}
                  >
                    <EditTaskFields
                      selectedEvent={selectedEvent}
                      selectedBarn={selectedBarn}
                      barnHorsesMap={barnHorsesMap}
                      getEvent={getEvent}
                      dueDate={dueDate}
                      setDueDate={setDueDate}
                      validation={validation}
                      setValidation={setValidation}
                      completeDate={completeDate}
                      handleCompleteDateChange={handleCompleteDateChange}
                      handleEventDataChange={handleEventDataChange}
                      currentStatusAssignment={currentStatusAssignment}
                      setCurrentStatusAssignment={setCurrentStatusAssignment}
                      scheduleAssignment={scheduleAssignment}
                      handleScheduleAssignmentChange={handleScheduleAssignmentChange}
                    />
                  </Box>
                </Paper>
                {
                  canAttachFiles
                  && selectedEvent.eventUploads
                  && selectedEvent.eventUploads.length > 0
                  && (
                    <Paper
                      sx={{ borderRadius: 3 }}
                    >
                      <List disablePadding>
                        <ListItem dense sx={listItemSx}>
                          <ListItemIcon sx={listItemIconSx}><AttachFile /></ListItemIcon>
                          <ListItemText>
                            <Property disableGutters label="Task Attachments">
                              {
                                selectedEvent.eventUploads.map((upload) => (
                                  <div
                                    className={classes.eventUploadContainer}
                                    key={upload.id}
                                  >
                                    <EventUpload eventUpload={upload} editable />
                                  </div>
                                ))
                              }
                            </Property>
                          </ListItemText>
                        </ListItem>
                      </List>
                    </Paper>
                  )
                }
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Button size="small" color="secondary" onClick={() => setEditMode(false)}>Cancel Edit Task</Button>
                </Box>
              </Stack>
            )
            : (
              <TaskHighlightComponent
                task={selectedEvent}
                currentStatusAssignment={currentStatusAssignment}
                detailedLocation={(event && event.resource && event.resource.location) || ''}
                scheduleAssignment={scheduleAssignment}
                canAttachFiles={canAttachFiles}
                handleClose={handleSafeClose}
                handleEventStatus={handleEventStatus}
                isUpdatingEvent={taskBusy}
                selectedEventBarnHorse={selectedEventBarnHorse}
                selectedEventLocation={selectedEventLocation}
                schedule={schedule}
                isAllDayScheduledTask={isAllDayScheduledTask}
                onEdit={() => setEditMode(true)}
                associatedTasks={associatedTasks}
                handleEventDataChange={handleEventDataChange}
              />
            )
        }

      </DialogContent>
      {
        editMode
        && (
          <DialogActions
            sx={{
              px: 1,
              flexDirection: 'column',
              gap: 1,
              width: '100%',
            }}
          >
            <Divider sx={{ width: '100%' }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '100%',
              }}
            >
              {
                openModalType === TASK_MODAL_TYPE && (
                  <Button color="secondary" sx={{ mr: 1 }} onClick={handleSafeClose}>Cancel</Button>
                )
              }
              {
                openModalType !== TASK_MODAL_TYPE && validation.hasChanged && (
                  <Button
                    color="secondary"
                    sx={{ mr: 1 }}
                    onClick={resetData}
                    disabled={taskBusy || !validation.name || !validation.completeDate || !validation.hasChanged}
                  >
                    Cancel
                  </Button>
                )
              }
              <Button
                onClick={handleSaveEdit}
                disabled={taskBusy || !validation.name || !validation.completeDate || !validation.hasChanged}
                color="primary"
                variant="contained"
              >
                Save
              </Button>
            </Box>
          </DialogActions>
        )
      }
      <AttachFileModal
        isOpen={openAttachFileModal}
        handleCloseModal={handleCloseAttachFileModal}
        eventId={selectedEvent.id}
      />
    </Box>
  );
  return (
    <HorseTaskReminders pairs={reminderPairs} open={!!openModalType} disabled={taskBusy} refreshKey={currentContext}>
      {taskContent}
    </HorseTaskReminders>
  );
}

EventDataCardComponent.propTypes = {
  selectedEvent: PropTypes.object,
  schedule: PropTypes.object,
  isCalendarEvent: PropTypes.bool,
  classes: PropTypes.object,
  tags: PropTypes.array,
  eventData: PropTypes.array,
  isRestricted: PropTypes.bool,
  canAttachFiles: PropTypes.bool,
  showLoadingIndicator: PropTypes.bool,
  getEvent: PropTypes.func,
  handleClose: PropTypes.any,
  isUpdatingEvent: PropTypes.bool,
  selectedBarn: PropTypes.object,
};

EventDataCardComponent.defaultProps = {
  selectedEvent: {},
  isCalendarEvent: false,
  schedule: null,
  classes: {},
  tags: [],
  eventData: [],
  selectedBarn: {},
  isRestricted: false,
  canAttachFiles: false,
  showLoadingIndicator: false,
  getEvent: () => { },
  handleClose: null,
  isUpdatingEvent: false,
};

export default EventDataCardComponent;
